import { useState } from "react";
import { ApiClientError, postReviewRule } from "../../../api/client";
import type { ReviewReplyStats, ReviewRule } from "../../../types/reviews";

const BAND_LABEL: Record<string, string> = { high: "★4〜5", mid: "★3", low: "★1〜2" };

interface RulesCardProps {
  rules: ReviewRule[];
  replyStats: ReviewReplyStats | null;
  onChanged: (rules: ReviewRule[]) => void;
}

export function RulesCard({ rules, replyStats, onChanged }: RulesCardProps) {
  const [busyBand, setBusyBand] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const toggle = async (band: ReviewRule["band"], enabled: boolean) => {
    setBusyBand(band);
    setError(null);
    try {
      const res = await postReviewRule({ band, enabled });
      onChanged(res.rules);
    } catch (e) {
      setError(e instanceof ApiClientError ? e.message : "更新に失敗しました。");
    } finally {
      setBusyBand(null);
    }
  };

  return (
    <div className="rv-card">
      <span className="rv-card-title">自動返信ルール</span>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {rules.map((r) => (
          <div className="rv-rule-row" key={r.band}>
            <div className="rv-rule-row-text">
              <span className="rv-rule-row-title">{BAND_LABEL[r.band] ?? r.band}</span>
              <span className="rv-rule-row-desc">{r.guidance}</span>
            </div>
            <button
              className={`rv-toggle ${r.enabled ? "on" : ""}`}
              disabled={busyBand === r.band}
              onClick={() => toggle(r.band, !r.enabled)}
              aria-label={`${BAND_LABEL[r.band]}の自動返信を${r.enabled ? "OFF" : "ON"}にする`}
            >
              <span className="rv-toggle-knob" />
            </button>
          </div>
        ))}
      </div>
      <span className="rv-note">自動ONの帯はAI文を翌朝そのまま投稿します。OFFの帯は下書き止まり（人の確認後に投稿）です。</span>
      {error && <span className="rv-note" style={{ color: "var(--rv-bad)" }}>{error}</span>}

      {replyStats && (
        <>
          <div style={{ borderTop: "1px solid var(--rv-track)", margin: "4px 0" }} />
          <span className="rv-card-title" style={{ fontSize: 13 }}>
            今月の自動返信
          </span>
          <div className="rv-store-mini-grid">
            <div className="rv-store-mini-item">
              <span className="rv-store-mini-label">返信済み / 総件数</span>
              <span className="rv-store-mini-value">
                {replyStats.posted} / {replyStats.total}
              </span>
            </div>
            <div className="rv-store-mini-item">
              <span className="rv-store-mini-label">平均返信時間</span>
              <span className="rv-store-mini-value">{replyStats.avgReplyHours !== null ? `${replyStats.avgReplyHours}h` : "―"}</span>
            </div>
            <div className="rv-store-mini-item">
              <span className="rv-store-mini-label">投稿待ち</span>
              <span className="rv-store-mini-value">{replyStats.queued}</span>
            </div>
            <div className="rv-store-mini-item">
              <span className="rv-store-mini-label">下書き / 失敗</span>
              <span className="rv-store-mini-value">
                {replyStats.draft} / {replyStats.failed}
              </span>
            </div>
          </div>
          <span className="rv-note">Amazonは公開返信不可のため、連携後は購入者メッセージで送信します（現在未連携）。</span>
        </>
      )}
    </div>
  );
}
